/**
 * Hook for unit photos management.
 * Handles: GET list, upload (presigned URL flow), delete, reorder, caption update.
 * Centrifugo: subscribes to unit-photos:{unitId} for real-time photo.approved and photo.rejected events.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { queryOptions, useQueryClient } from '@tanstack/react-query';
import { Centrifuge, type Subscription } from 'centrifuge';
import { subscribeChannel, unsubscribeChannel } from '@resida/realtime-client';
import { apiFetch, getCentrifugoToken } from '../lib/api';
import type { UnitPhoto, PendingPhoto, PhotoCentrifugoEvent } from '../types/unit-marketing.types';
import { readFileIntoMemory } from '@resida/shared';

const CENTRIFUGO_WS_URL = import.meta.env.VITE_CENTRIFUGO_WS_URL ||
  `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/centrifugo/connection/websocket`;

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

const unitPhotosKey = (unitId: string) => ['unit-photos', unitId] as const;

/**
 * The unit's photos as a query. useUnitPhotos mirrors its live state into this cache entry, so
 * readers that only need the list (the guide rail's completeness) see approvals, deletes and
 * reorders made through the photo grid without opening a second realtime connection.
 */
export function unitPhotosQueryOptions(unitId: string) {
  return queryOptions({
    queryKey: unitPhotosKey(unitId),
    queryFn: async (): Promise<UnitPhoto[]> => {
      const photos = await apiFetch<UnitPhoto[]>(`/landlord/api/units/${unitId}/photos`);
      return Array.isArray(photos) ? photos : [];
    },
    enabled: !!unitId,
  });
}

interface UseUnitPhotosReturn {
  photos: UnitPhoto[];
  pendingPhotos: PendingPhoto[];
  isLoading: boolean;
  uploadError: string | null;
  fetchError: string | null;
  rejectionMessage: string | null;
  clearRejectionMessage: () => void;
  uploadPhoto: (file: File) => Promise<void>;
  uploadPhotos: (files: File[], maxAdditional: number) => Promise<number>;
  deletePhoto: (photoId: string) => Promise<void>;
  reorderPhotos: (orderedIds: string[]) => Promise<void>;
  updateCaption: (photoId: string, caption: string | null) => Promise<void>;
  setPrimaryPhoto: (photoId: string) => Promise<void>;
  refetchPhotos: () => Promise<void>;
}

// A failed `fetch` throws a bare TypeError ("Failed to fetch"), which says nothing a landlord can act on.
function describeFailure(err: unknown, fallback: string): string {
  if (err instanceof TypeError) return 'Check your connection and try again.';
  if (err instanceof Error && !err.message.startsWith('S3 upload failed')) return err.message;
  return fallback;
}

export function useUnitPhotos(unitId: string): UseUnitPhotosReturn {
  const queryClient = useQueryClient();
  const [photos, setPhotos] = useState<UnitPhoto[]>([]);
  const [pendingPhotos, setPendingPhotos] = useState<PendingPhoto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [rejectionMessage, setRejectionMessage] = useState<string | null>(null);

  const centrifugeRef = useRef<Centrifuge | null>(null);
  const subscriptionRef = useRef<Subscription | null>(null);

  // Store callbacks in refs so they're always up to date in the subscription
  const photosRef = useRef<UnitPhoto[]>([]);
  const pendingPhotosRef = useRef<PendingPhoto[]>([]);
  photosRef.current = photos;
  pendingPhotosRef.current = pendingPhotos;

  // ── Fetch photos ──────────────────────────────────────────────────────────

  const fetchPhotos = useCallback(async () => {
    if (!unitId) return;
    try {
      setIsLoading(true);
      setFetchError(null);
      const photos = await apiFetch<UnitPhoto[]>(
        `/landlord/api/units/${unitId}/photos`
      );
      setPhotos(Array.isArray(photos) ? photos : []);
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : 'Failed to load photos');
      setPhotos([]);
    } finally {
      setIsLoading(false);
    }
  }, [unitId]);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  // Mirror the loaded list into the shared query cache (see unitPhotosQueryOptions).
  useEffect(() => {
    if (!unitId || isLoading || fetchError) return;
    queryClient.setQueryData(unitPhotosKey(unitId), photos);
  }, [unitId, photos, isLoading, fetchError, queryClient]);

  // ── Centrifugo subscription ───────────────────────────────────────────────

  useEffect(() => {
    if (!unitId) return;

    let cancelled = false;

    const connectAndSubscribe = async () => {
      try {
        const { token } = await getCentrifugoToken();
        if (cancelled) return;

        const client = new Centrifuge(CENTRIFUGO_WS_URL, { token });
        centrifugeRef.current = client;

        const channel = `unit-photos:${unitId}`;
        const sub = subscribeChannel(client, channel);
        subscriptionRef.current = sub;

        const handlePhotoEvent = (event: PhotoCentrifugoEvent) => {
          if (event.type === 'photo.approved') {
            setPendingPhotos(prev => prev.filter(p => p.tempId !== event.photoId));

            const approvedPhoto: UnitPhoto = {
              id: event.photoId,
              unitId,
              storageKey: '',
              url: event.url,
              thumbnailUrl: event.thumbnailUrl,
              displayOrder: event.displayOrder,
              caption: null,
              status: 'approved',
              aiCategory: event.aiCategory,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            };

            setPhotos(prev => {
              const exists = prev.some(p => p.id === event.photoId);
              if (exists) {
                return prev.map(p => p.id === event.photoId ? approvedPhoto : p);
              }
              return [...prev, approvedPhoto].sort((a, b) => a.displayOrder - b.displayOrder);
            });
          } else if (event.type === 'photo.rejected') {
            setPendingPhotos(prev => prev.filter(p => p.tempId !== event.photoId));
            setRejectionMessage(event.reason || 'Your photo was rejected.');
          }
        };

        sub.on('publication', (ctx) => {
          handlePhotoEvent(ctx.data as PhotoCentrifugoEvent);
        });

        sub.on('subscribed', async (ctx) => {
          // If Centrifugo did not auto-recover (new subscription or no prior position),
          // fetch recent history to catch any photo.rejected events published while
          // the component was unmounted (e.g. during a DLQ retry delay).
          if (!ctx.recovered) {
            try {
              const result = await sub.history({ limit: 20 });
              for (const pub of result.publications) {
                handlePhotoEvent(pub.data as PhotoCentrifugoEvent);
              }
            } catch {
              // History unavailable — no-op; live events still work
            }
          }
        });

        sub.on('error', (ctx) => {
          console.warn('unit-photos subscription error:', ctx);
        });

        sub.subscribe();
        client.connect();
      } catch (err) {
        console.warn('Failed to connect to Centrifugo for unit-photos:', err);
      }
    };

    connectAndSubscribe();

    return () => {
      cancelled = true;
      if (centrifugeRef.current) {
        unsubscribeChannel(centrifugeRef.current, subscriptionRef.current);
      }
      subscriptionRef.current = null;
      if (centrifugeRef.current) {
        centrifugeRef.current.disconnect();
        centrifugeRef.current = null;
      }
    };
  }, [unitId]);

  // ── Upload flow ───────────────────────────────────────────────────────────

  // Returns null on success, or an error message on failure. Does NOT touch
  // `uploadError` itself — callers own the shared error state so a batch of
  // several files can aggregate per-file failures instead of one file's
  // start silently clobbering a previous file's error (FM5).
  const uploadOneFile = useCallback(async (file: File): Promise<string | null> => {
    // Client-side validation
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return 'Unsupported file type. Please upload a JPEG, PNG, WebP, or HEIC image.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File is too large. Maximum size is 50 MB.';
    }

    // Read the bytes now, while the selection is fresh: a Photos-library / iCloud file can't be
    // re-read when fetch streams the PUT body later (opaque net::ERR_ACCESS_DENIED).
    let body: File;
    try {
      body = await readFileIntoMemory(file);
    } catch (err) {
      return describeFailure(err, 'We could not read this file.');
    }

    // Step 1: Get presigned URL
    let photoId: string;
    let uploadUrl: string;
    try {
      const response = await apiFetch<{ photoId: string; uploadUrl: string }>(
        `/landlord/api/units/${unitId}/photos/upload-url`,
        {
          method: 'POST',
          body: JSON.stringify({ filename: file.name, contentType: file.type }),
        }
      );
      photoId = response.photoId;
      uploadUrl = response.uploadUrl;
    } catch (err) {
      return describeFailure(err, 'We could not start the upload. Try again.');
    }

    // Step 2: PUT directly to S3 (no auth header — presigned URL handles auth)
    try {
      const s3Response = await fetch(uploadUrl, {
        method: 'PUT',
        body,
        headers: { 'Content-Type': file.type },
      });
      if (!s3Response.ok) {
        throw new Error(`S3 upload failed: ${s3Response.status}`);
      }
    } catch (err) {
      return describeFailure(err, 'The upload was interrupted. Try again.');
    }

    // Step 3: Confirm upload
    try {
      await apiFetch<unknown>(
        `/landlord/api/units/${unitId}/photos/${photoId}/confirm`,
        { method: 'POST' }
      );
    } catch (err) {
      return describeFailure(err, 'We could not finish the upload. Try again.');
    }

    // Step 4: Add pending entry to local state — spinner shown until Centrifugo event arrives
    const localPreviewUrl = URL.createObjectURL(file);
    setPendingPhotos(prev => [...prev, { tempId: photoId, file, localPreviewUrl }]);
    return null;
  }, [unitId]);

  const uploadPhoto = useCallback(async (file: File) => {
    setUploadError(null);
    const error = await uploadOneFile(file);
    if (error) {
      setUploadError(error);
    }
  }, [uploadOneFile]);

  const uploadPhotos = useCallback(async (files: File[], maxAdditional: number) => {
    setUploadError(null);
    const accepted = files.slice(0, Math.max(0, maxAdditional));
    // Aggregate failures across the whole batch rather than a single shared
    // string any file's start could erase — a mid-batch failure must still
    // be visible once the batch settles, even if a later file succeeds (AC-5).
    const failures: string[] = [];
    for (const file of accepted) {
      try {
        const error = await uploadOneFile(file);
        if (error) {
          failures.push(`${file.name} didn't upload. ${error}`);
        }
      } catch (err) {
        failures.push(`${file.name} didn't upload. ${describeFailure(err, 'Try again.')}`);
      }
    }
    if (failures.length > 0) {
      setUploadError(failures.join(' • '));
    }
    return files.length - accepted.length;
  }, [uploadOneFile]);

  // ── Delete ────────────────────────────────────────────────────────────────

  const deletePhoto = useCallback(async (photoId: string) => {
    await apiFetch<unknown>(
      `/landlord/api/units/${unitId}/photos/${photoId}`,
      { method: 'DELETE' }
    );
    setPhotos(prev => prev.filter(p => p.id !== photoId));
  }, [unitId]);

  // ── Reorder ───────────────────────────────────────────────────────────────

  const reorderPhotos = useCallback(async (orderedIds: string[]) => {
    // Optimistic update
    setPhotos(prev => {
      const byId = new Map<string, UnitPhoto>(prev.map(p => [p.id, p]));
      return orderedIds
        .filter(id => byId.has(id))
        .map((id, index) => {
          const photo = byId.get(id) as UnitPhoto;
          return { ...photo, displayOrder: index };
        });
    });

    await apiFetch<unknown>(
      `/landlord/api/units/${unitId}/photos/order`,
      {
        method: 'PATCH',
        body: JSON.stringify({ orderedIds }),
      }
    );
  }, [unitId]);

  // ── Caption update ────────────────────────────────────────────────────────

  const updateCaption = useCallback(async (photoId: string, caption: string | null) => {
    setPhotos(prev =>
      prev.map(p => p.id === photoId ? { ...p, caption } : p)
    );
    await apiFetch<unknown>(
      `/landlord/api/units/${unitId}/photos/${photoId}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ caption }),
      }
    );
  }, [unitId]);

  const clearRejectionMessage = useCallback(() => {
    setRejectionMessage(null);
  }, []);

  // ── Set primary ───────────────────────────────────────────────────────────

  const setPrimaryPhoto = useCallback(async (photoId: string) => {
    const approvedPhotos = photos.filter(p => p.status === 'approved');
    const orderedIds = [
      photoId,
      ...approvedPhotos.filter(p => p.id !== photoId).map(p => p.id),
    ];
    await reorderPhotos(orderedIds);
  }, [photos, reorderPhotos]);

  return {
    photos,
    pendingPhotos,
    isLoading,
    uploadError,
    fetchError,
    rejectionMessage,
    clearRejectionMessage,
    uploadPhoto,
    uploadPhotos,
    deletePhoto,
    reorderPhotos,
    updateCaption,
    setPrimaryPhoto,
    refetchPhotos: fetchPhotos,
  };
}
